/**
 * Tripnovac Visa — Country Detail Page (Production Design)
 * Full-width hero banner, 2-column layout with government sources,
 * special entry rules, embassy network, and status card.
 *
 * @company Alpha Libra
 * @domain tripnovac-visa.com
 */

import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Container, Typography, Grid, Card, CardContent,
  Button, Chip, Paper, Skeleton, Alert,
  List, ListItem, ListItemIcon, ListItemText, Divider,
} from '@mui/material';
import {
  ArrowBack, CheckCircle, Language, Launch, LocationOn,
  FlightTakeoff, DirectionsBoat, DirectionsCar,
  Gavel, Assignment,
  NotificationsActive, RecordVoiceOver, ListAlt,
} from '@mui/icons-material';
import { useParams, useNavigate } from 'react-router-dom';
import { API_BASE_URL } from '../../constants';

/* ------------------------------------------------------------------ */
/*  Types                                                              */
/* ------------------------------------------------------------------ */

interface CountryData {
  id: number;
  name: string;
  iso2: string;
  iso3: string;
  region?: string;
  subregion?: string;
  capital?: string;
  flagEmoji?: string;
  visaTypeCount?: number;
}

interface VisaType {
  id: string;
  name: string;
  category: string;
  processingAvgDays?: number;
  validityDays?: number;
  stayDays?: number;
  feeAmount?: number;
  feeCurrency?: string;
  description?: string;
  online?: boolean;
  multipleEntry?: boolean;
  entryType?: string;
  isActive?: boolean;
}

interface GovSource {
  name: string;
  type: string;
  url: string;
}

/* ------------------------------------------------------------------ */
/*  Entry point icon helper                                            */
/* ------------------------------------------------------------------ */

const entryIcon = (type: string) => {
  if (type === 'airport') return <FlightTakeoff fontSize="small" />;
  if (type === 'seaport') return <DirectionsBoat fontSize="small" />;
  return <DirectionsCar fontSize="small" />;
};

/* ------------------------------------------------------------------ */
/*  Component                                                          */
/* ------------------------------------------------------------------ */

const CountryDetailPage: React.FC = () => {
  const { countryCode } = useParams<{ countryCode: string }>();
  const navigate = useNavigate();

  const [country, setCountry] = useState<CountryData | null>(null);
  const [visaTypes, setVisaTypes] = useState<VisaType[]>([]);
  const [govSources, setGovSources] = useState<GovSource[]>([]);
  const [embassies, setEmbassies] = useState<{country: string; city: string}[]>([]);
  const [entryPoints, setEntryPoints] = useState<{type: string; name: string}[]>([]);
  const [specialRules] = useState<{label: string; detail: string}[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  /* ---- data fetching ---- */
  const fetchAll = useCallback(async () => {
    if (!countryCode) return;
    setLoading(true);
    setError(null);

    try {
      // 1) Country core data
      const cRes = await fetch(`${API_BASE_URL}/countries/${countryCode}`);
      const cJson = await cRes.json();

      if (!cJson.success) { setError('Country not found'); setLoading(false); return; }
      const c: CountryData = cJson.data;
      setCountry(c);

      // 2) Parallel: visa types + government sources
      const [vtRes, gsRes, completeRes] = await Promise.all([
        fetch(`${API_BASE_URL}/countries/${c.id}/visa-types`).catch(() => null),
        fetch(`${API_BASE_URL}/registry/countries/${c.iso2}/sources`).catch(() => null),
        fetch(`${API_BASE_URL}/countries/${c.iso2}/complete`).catch(() => null),
      ]);

      if (vtRes) {
        const vtJson = await vtRes.json().catch(() => ({ success: false }));
        if (vtJson.success) setVisaTypes(vtJson.data ?? []);
      }

      if (gsRes) {
        const gsJson = await gsRes.json().catch(() => ({ official: [] }));
        setGovSources(gsJson.official ?? []);
      }

      // Complete country data (embassies from embassy_contacts / Consulate)
      if (completeRes?.ok) {
        const compJson = await completeRes.json().catch(() => ({}));
        const compData = compJson.data || compJson;

        // Parse embassies / consulates
        const embData = compData.embassies || compData.Consulate || [];
        if (embData.length) {
          setEmbassies(embData.map((e: any) => ({
            country: e.name || e.embassy_name || '',
            city: e.city || '',
          })));
        }

        // Parse entry points from visa types' entryPoints JSON field
        const allEntryPoints: {type: string; name: string}[] = [];
        const vts = compData.VisaType || compData.visaTypes || [];
        for (const vt of vts) {
          if (vt.entryPoints && Array.isArray(vt.entryPoints)) {
            for (const ep of vt.entryPoints) {
              allEntryPoints.push({ type: ep.type || 'land', name: ep.name || ep.port_name || '' });
            }
          }
        }
        if (allEntryPoints.length) setEntryPoints(allEntryPoints);

        // If visa types weren't loaded from the other endpoint, use these
        if (visaTypes.length === 0 && vts.length > 0) {
          setVisaTypes(vts);
        }
      }
    } catch {
      setError('Failed to load country data');
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [countryCode]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  /* ---- derived ---- */
  const iso2 = country?.iso2 ?? countryCode?.toUpperCase() ?? '';
  const iso3 = country?.iso3 ?? '';
  const flagEmoji = country?.flagEmoji ?? '';
  const visaCount = visaTypes.length || country?.visaTypeCount || 0;
  const hasEVisa = visaTypes.some(v => /e.?visa/i.test(v.name));
  const embassyCount = embassies.length;
  const entryPointCount = entryPoints.length;

  /* ================================================================ */
  /*  Loading skeleton                                                 */
  /* ================================================================ */
  if (loading) {
    return (
      <Box sx={{ minHeight: '100vh', bgcolor: '#030B1B' }}>
        <Skeleton variant="rectangular" height={220} sx={{ bgcolor: 'rgba(255,255,255,0.04)' }} />
        <Container maxWidth="lg" sx={{ py: 4 }}>
          <Grid container spacing={4}>
            <Grid item xs={12} md={8}>
              <Skeleton variant="rectangular" height={200} sx={{ borderRadius: 3, mb: 3, bgcolor: 'rgba(255,255,255,0.04)' }} />
              <Skeleton variant="rectangular" height={200} sx={{ borderRadius: 3, bgcolor: 'rgba(255,255,255,0.04)' }} />
            </Grid>
            <Grid item xs={12} md={4}>
              <Skeleton variant="rectangular" height={350} sx={{ borderRadius: 3, bgcolor: 'rgba(255,255,255,0.04)' }} />
            </Grid>
          </Grid>
        </Container>
      </Box>
    );
  }

  /* ================================================================ */
  /*  Error state                                                      */
  /* ================================================================ */
  if (error || !country) {
    return (
      <Container maxWidth="lg" sx={{ py: 6 }}>
        <Alert severity="error" sx={{ mb: 3 }}>{error || 'Country not found'}</Alert>
        <Button startIcon={<ArrowBack />} onClick={() => navigate('/countries')}>
          Back to Countries
        </Button>
      </Container>
    );
  }

  /* ================================================================ */
  /*  Main render                                                      */
  /* ================================================================ */
  return (
    <Box sx={{ minHeight: '100vh', bgcolor: '#030B1B' }}>

      {/* ============================================================ */}
      {/*  HERO BANNER                                                  */}
      {/* ============================================================ */}
      <Box
        sx={{
          position: 'relative',
          width: '100%',
          minHeight: 200,
          backgroundImage: 'url("https://images.unsplash.com/photo-1526778548025-fa2f459cd5c1?w=1920&q=80")',
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          color: '#fff',
          display: 'flex',
          alignItems: 'center',
          overflow: 'hidden',
          pb: 7,
          '&::before': {
            content: '""',
            position: 'absolute',
            inset: 0,
            background: 'linear-gradient(180deg, rgba(3,11,27,0.82) 0%, rgba(3,11,27,0.92) 100%)',
            zIndex: 0,
          },
        }}
      >

        <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1, py: 4 }}>
          <Button
            startIcon={<ArrowBack />}
            onClick={() => navigate('/countries')}
            sx={{ color: 'rgba(255,255,255,0.9)', mb: 2, textTransform: 'none', textShadow: '0 2px 8px rgba(0,0,0,0.4)', '&:hover': { color: '#fff' } }}
          >
            All Countries
          </Button>

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2.5, flexWrap: 'wrap' }}>
            {/* Flag */}
            <Box
              sx={{
                width: 72, height: 52, borderRadius: 1.5,
                bgcolor: 'rgba(255,255,255,0.15)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontSize: 36, boxShadow: '0 2px 8px rgba(0,0,0,0.25)',
                border: '2px solid rgba(255,255,255,0.25)',
              }}
            >
              {flagEmoji || iso2}
            </Box>

            <Box sx={{ flex: 1 }}>
              <Typography variant="h3" fontWeight={800} sx={{ lineHeight: 1.15, letterSpacing: '-0.02em', textShadow: '0 2px 8px rgba(0,0,0,0.4)' }}>
                {country.name}
                <Typography component="span" variant="h5" fontWeight={400} sx={{ ml: 1.5, opacity: 0.85 }}>
                  — Live Visa Application
                </Typography>
              </Typography>

              <Typography variant="body1" sx={{ mt: 0.5, opacity: 0.8, textShadow: '0 1px 4px rgba(0,0,0,0.4)' }}>
                Country Code: {iso3 || iso2}
                {country.subregion && <> &nbsp;|&nbsp; {country.subregion}</>}
                {country.region && !country.subregion && <> &nbsp;|&nbsp; {country.region}</>}
              </Typography>

              <Typography variant="body2" sx={{ mt: 1.5, opacity: 0.85, maxWidth: 700, lineHeight: 1.6 }}>
                Country-by-country, unique, dynamic implementation. Government-verified visa data with live application
                processing{visaTypes.length > 0 ? ` for ${visaTypes.length} visa types` : ''}. E-visa available for eligible nationalities.
              </Typography>
            </Box>
          </Box>
        </Container>
      </Box>

      {/* ============================================================ */}
      {/*  MAIN CONTENT                                                 */}
      {/* ============================================================ */}
      <Container maxWidth="lg" sx={{ py: 5 }}>
        <Grid container spacing={4}>

          {/* ======================================================== */}
          {/*  LEFT COLUMN                                              */}
          {/* ======================================================== */}
          <Grid item xs={12} md={8}>

            {/* ---- Official Government Sources ---- */}
            <Paper sx={{ p: 3.5, borderRadius: 3, mb: 4, bgcolor: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.06)', backgroundImage: 'none' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2.5 }}>
                <Language sx={{ color: '#0EA5E9' }} />
                <Typography variant="h6" fontWeight={700} sx={{ color: 'white' }}>Official Government Sources</Typography>
              </Box>

              {govSources.length > 0 ? (
                <List disablePadding>
                  {govSources.map((s, i) => (
                    <React.Fragment key={s.url}>
                      <ListItem
                        component="a"
                        href={s.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        sx={{
                          px: 2, py: 1.5, borderRadius: 2,
                          '&:hover': { bgcolor: 'rgba(255,255,255,0.04)' },
                          textDecoration: 'none', color: 'inherit',
                        }}
                      >
                        <ListItemIcon sx={{ minWidth: 36 }}>
                          <Launch fontSize="small" sx={{ color: '#0EA5E9' }} />
                        </ListItemIcon>
                        <ListItemText
                          primary={s.name}
                          secondary={s.type}
                          primaryTypographyProps={{ fontWeight: 600, fontSize: '0.95rem', color: '#0EA5E9' }}
                          secondaryTypographyProps={{ color: 'rgba(255,255,255,0.55)' }}
                        />
                      </ListItem>
                      {i < govSources.length - 1 && <Divider component="li" sx={{ borderColor: 'rgba(255,255,255,0.06)' }} />}
                    </React.Fragment>
                  ))}
                </List>
              ) : (
                <List disablePadding>
                  {[
                    { name: `${country.name} Ministry of Foreign Affairs`, type: 'Government Portal' },
                    { name: `E-${country.name} Portal`, type: 'E-Services' },
                    { name: 'Schengen Visa Info', type: 'Regional Information' },
                    { name: 'US Embassy', type: 'Embassy Portal' },
                  ].map((s, i, arr) => (
                    <React.Fragment key={s.name}>
                      <ListItem sx={{ px: 2, py: 1.5, borderRadius: 2 }}>
                        <ListItemIcon sx={{ minWidth: 36 }}>
                          <Launch fontSize="small" sx={{ color: '#0EA5E9' }} />
                        </ListItemIcon>
                        <ListItemText
                          primary={s.name}
                          secondary={s.type}
                          primaryTypographyProps={{ fontWeight: 600, fontSize: '0.95rem', color: '#0EA5E9' }}
                          secondaryTypographyProps={{ color: 'rgba(255,255,255,0.55)' }}
                        />
                      </ListItem>
                      {i < arr.length - 1 && <Divider component="li" sx={{ borderColor: 'rgba(255,255,255,0.06)' }} />}
                    </React.Fragment>
                  ))}
                </List>
              )}
            </Paper>

            {/* ---- Special Entry Rules ---- */}
            {specialRules.length > 0 && (
            <Paper sx={{ p: 3.5, borderRadius: 3, mb: 4, bgcolor: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.06)', backgroundImage: 'none' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2.5 }}>
                <Gavel sx={{ color: '#F59E0B' }} />
                <Typography variant="h6" fontWeight={700} sx={{ color: 'white' }}>Special Entry Rules</Typography>
              </Box>

              <Grid container spacing={2}>
                {specialRules.map((r) => (
                  <Grid item xs={12} sm={6} key={r.label}>
                    <Card
                      variant="outlined"
                      sx={{
                        p: 2, height: '100%', borderRadius: 2,
                        bgcolor: 'rgba(255,255,255,0.03)',
                        borderColor: 'rgba(255,255,255,0.06)',
                        '&:hover': { borderColor: '#0EA5E9', boxShadow: '0 2px 12px rgba(14,165,233,0.12)' },
                        transition: 'all 0.2s',
                      }}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                        <Box sx={{
                          width: 32, height: 32, borderRadius: 1,
                          bgcolor: 'rgba(245,158,11,0.15)', display: 'flex', alignItems: 'center', justifyContent: 'center',
                          color: '#F59E0B',
                        }}>
                          <Gavel fontSize="small" />
                        </Box>
                        <Typography variant="subtitle2" fontWeight={700} sx={{ color: 'white' }}>{r.label}</Typography>
                      </Box>
                      <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.55)' }}>{r.detail}</Typography>
                    </Card>
                  </Grid>
                ))}
              </Grid>
            </Paper>
            )}

            {/* ---- Embassy Network & Entry Points ---- */}
            {(embassies.length > 0 || entryPoints.length > 0) && (
            <Paper sx={{ p: 3.5, borderRadius: 3, mb: 4, bgcolor: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.06)', backgroundImage: 'none' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2.5 }}>
                <LocationOn sx={{ color: '#0EA5E9' }} />
                <Typography variant="h6" fontWeight={700} sx={{ color: 'white' }}>Embassy Network &amp; Entry Points</Typography>
              </Box>

              <Grid container spacing={4}>
                {/* Embassies */}
                {embassies.length > 0 && (
                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1.5, color: 'rgba(255,255,255,0.7)' }}>
                    Embassy Locations
                  </Typography>
                  <List disablePadding dense>
                    {embassies.map((e) => (
                      <ListItem key={e.country + e.city} sx={{ px: 1, py: 0.75 }}>
                        <ListItemIcon sx={{ minWidth: 28 }}>
                          <LocationOn fontSize="small" sx={{ color: 'rgba(255,255,255,0.4)' }} />
                        </ListItemIcon>
                        <ListItemText
                          primary={`${e.country} — ${e.city}`}
                          primaryTypographyProps={{ variant: 'body2', color: 'rgba(255,255,255,0.7)' }}
                        />
                      </ListItem>
                    ))}
                  </List>
                </Grid>
                )}

                {/* Entry Points */}
                {entryPoints.length > 0 && (
                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1.5, color: 'rgba(255,255,255,0.7)' }}>
                    Entry Points
                  </Typography>
                  <List disablePadding dense>
                    {entryPoints.map((ep) => (
                      <ListItem key={ep.name} sx={{ px: 1, py: 0.75 }}>
                        <ListItemIcon sx={{ minWidth: 28, color: 'rgba(255,255,255,0.4)' }}>
                          {entryIcon(ep.type)}
                        </ListItemIcon>
                        <ListItemText
                          primary={ep.name}
                          primaryTypographyProps={{ variant: 'body2', color: 'rgba(255,255,255,0.7)' }}
                        />
                      </ListItem>
                    ))}
                  </List>
                </Grid>
                )}
              </Grid>
            </Paper>
            )}
          </Grid>

          {/* ======================================================== */}
          {/*  RIGHT COLUMN — Status Card                               */}
          {/* ======================================================== */}
          <Grid item xs={12} md={4}>
            <Paper
              sx={{
                borderRadius: 3, overflow: 'hidden',
                position: 'sticky', top: 90,
                bgcolor: 'rgba(255,255,255,0.03)',
                border: '1px solid rgba(255,255,255,0.06)',
                backgroundImage: 'none',
              }}
            >
              {/* Header */}
              <Box
                sx={{
                  px: 3, py: 2.5,
                  background: 'linear-gradient(135deg, #0EA5E9 0%, #14B8A6 100%)',
                  color: '#fff',
                }}
              >
                <Typography variant="h6" fontWeight={700}>Current Status</Typography>
              </Box>

              <CardContent sx={{ p: 3 }}>
                {/* Green chips */}
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 3 }}>
                  <Chip
                    label={`${visaCount} Visa Types Available`}
                    size="small"
                    sx={{ bgcolor: 'rgba(16,185,129,0.15)', color: '#10B981', fontWeight: 600, border: '1px solid rgba(16,185,129,0.3)' }}
                  />
                  {hasEVisa && (
                    <Chip
                      label="E-Visa Available"
                      size="small"
                      sx={{ bgcolor: 'rgba(16,185,129,0.15)', color: '#10B981', fontWeight: 600, border: '1px solid rgba(16,185,129,0.3)' }}
                    />
                  )}
                  <Chip
                    label="Visa-Free for 100+ Countries"
                    size="small"
                    sx={{ bgcolor: 'rgba(16,185,129,0.15)', color: '#10B981', fontWeight: 600, border: '1px solid rgba(16,185,129,0.3)' }}
                  />
                </Box>

                {/* Checklist */}
                <List disablePadding dense>
                  {[
                    'Government-Verified Data',
                    'Live Application Processing',
                    `${embassyCount} Embassy Locations Worldwide`,
                    `${entryPointCount} Official Entry Points`,
                  ].map((text) => (
                    <ListItem key={text} sx={{ px: 0, py: 0.75 }}>
                      <ListItemIcon sx={{ minWidth: 30 }}>
                      <CheckCircle fontSize="small" sx={{ color: '#10B981' }} />
                    </ListItemIcon>
                      <ListItemText
                        primary={text}
                        primaryTypographyProps={{ variant: 'body2', fontWeight: 500, color: 'rgba(255,255,255,0.7)' }}
                      />
                    </ListItem>
                  ))}
                </List>

                <Divider sx={{ my: 2.5, borderColor: 'rgba(255,255,255,0.06)' }} />

                {/* Visa types quick list */}
                {visaTypes.length > 0 && (
                  <>
                    <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1.5, color: 'white' }}>
                      Available Visa Types
                    </Typography>
                    {visaTypes.map((v) => (
                      <Box
                        key={v.id}
                        onClick={() => navigate(`/countries/${iso2}/visas/${v.id}`)}
                        sx={{
                          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                          py: 1, px: 1.5, mb: 1, borderRadius: 1.5,
                          border: '1px solid', borderColor: 'rgba(255,255,255,0.06)',
                          bgcolor: 'rgba(255,255,255,0.02)',
                          '&:hover': { borderColor: '#0EA5E9', bgcolor: 'rgba(14,165,233,0.08)' },
                          transition: 'all 0.15s', cursor: 'pointer',
                        }}
                      >
                        <Box>
                          <Typography variant="body2" fontWeight={600} sx={{ color: 'white' }}>{v.name}</Typography>
                          <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.55)' }}>
                            {v.processingAvgDays ? `${v.processingAvgDays} days` : ''}
                            {v.processingAvgDays && v.validityDays ? ' · ' : ''}
                            {v.validityDays ? `${v.validityDays}d validity` : ''}
                          </Typography>
                        </Box>
                        {v.feeAmount != null && v.feeAmount > 0 && (
                          <Chip label={`$${v.feeAmount}`} size="small" sx={{ bgcolor: 'rgba(14,165,233,0.15)', color: '#0EA5E9', border: '1px solid rgba(14,165,233,0.3)', fontWeight: 600 }} />
                        )}
                      </Box>
                    ))}
                  </>
                )}
              </CardContent>
            </Paper>
          </Grid>
        </Grid>

        {/* ============================================================ */}
        {/*  BOTTOM — Visa Hub Support Tools                             */}
        {/* ============================================================ */}
        <Paper
          sx={{
            mt: 2, p: { xs: 3, md: 4 }, borderRadius: 3,
            background: 'linear-gradient(135deg, #0F172A 0%, #1E293B 50%, rgba(14,165,233,0.15) 100%)',
            border: '1px solid rgba(255,255,255,0.06)',
            backgroundImage: 'none',
            bgcolor: 'transparent',
            color: '#fff',
          }}
          style={{ background: 'linear-gradient(135deg, #0F172A 0%, #1E293B 50%, rgba(14,165,233,0.15) 100%)' }}
        >
          <Typography variant="h5" fontWeight={700} gutterBottom>
            Visa Hub — Additional Support Tools
          </Typography>
          <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.55)', mb: 3, maxWidth: 600 }}>
            Prepare for your visa journey with interview practice, document checklists, reminders, and country-specific guidance.
          </Typography>

          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
            <Button
              variant="contained"
              startIcon={<RecordVoiceOver />}
              onClick={() => navigate(`/interview-prep/${iso2}`)}
              sx={{ bgcolor: 'rgba(255,255,255,0.15)', '&:hover': { bgcolor: 'rgba(255,255,255,0.25)' }, textTransform: 'none', fontWeight: 600 }}
            >
              Interview Prep
            </Button>
            <Button
              variant="contained"
              startIcon={<Assignment />}
              onClick={() => navigate(`/documents/checklist/${iso2}`)}
              sx={{ bgcolor: 'rgba(255,255,255,0.15)', '&:hover': { bgcolor: 'rgba(255,255,255,0.25)' }, textTransform: 'none', fontWeight: 600 }}
            >
              Document Checklist
            </Button>
            <Button
              variant="contained"
              startIcon={<NotificationsActive />}
              onClick={() => navigate(`/reminders?country=${iso2}`)}
              sx={{ bgcolor: 'rgba(255,255,255,0.15)', '&:hover': { bgcolor: 'rgba(255,255,255,0.25)' }, textTransform: 'none', fontWeight: 600 }}
            >
              Set Reminders
            </Button>
            <Button
              variant="contained"
              startIcon={<ListAlt />}
              onClick={() => navigate(`/country-hub/${iso2}`)}
              sx={{ bgcolor: 'rgba(255,255,255,0.15)', '&:hover': { bgcolor: 'rgba(255,255,255,0.25)' }, textTransform: 'none', fontWeight: 600 }}
            >
              Country Hub
            </Button>
          </Box>
        </Paper>
      </Container>
    </Box>
  );
};

export default CountryDetailPage;
